import type { AccountSource } from "owa-account-source-list-store";
import { getAccountKeyForMailboxInfo, type MailboxInfo } from "owa-client-types";
import registerForAccountStartupComplete from "./registerForAccountStartupComplete";
import startTimeout from "./startTimeout";

/**
 * Interface used to track the waiting for start info, it includes the resolve function of the
 * waiting promise and the cancel function for the pending timeout
 */
interface WaitingForStartInfo {
    resolve: (isStarted: boolean) => void;
    cancelTimeout: () => void;
}

/**
 * The StartedAccountMonitor class is used to track callers that are waiting for an account to be started.
 */
class StartedAccountMonitor {
    // Set of accountKeys that are already started
    private startedAccountKeys: Set<string> = new Set<string>();
    
    // Map of waiting promises for accounts that are not yet started, these are keyed by the accountKey
    private pendingStartWaitsByAccountKey: Map<string, WaitingForStartInfo[]> = new Map<string, WaitingForStartInfo[]>();
    
    /**
     * Constructor for the StartedAccountMonitor class, which will register for account startup complete
     * and handle telling pending waiters that the account has started.
     */
    constructor() {
        const onAccountStarted = (accountSource: AccountSource) => {
            const accountKey = getAccountKeyForMailboxInfo(accountSource.mailboxInfo);
            this.startedAccountKeys.add(accountKey);
            const waiters = this.pendingStartWaitsByAccountKey.get(accountKey);
            
            if (waiters) {
                for (const waiter of waiters) {
                    waiter.cancelTimeout();
                    waiter.resolve(true);
                }
                
                this.pendingStartWaitsByAccountKey.delete(accountKey);
            }
        };
        
        registerForAccountStartupComplete(onAccountStarted);
    }
    
    /**
     * Waits for the specified account to be started, or times out after the specified number of milliseconds.
     * @param accountKey AccountKey associated with the account
     * @param millisecondsTimeout The number of milliseconds to wait before timing out, when timed out the promise will resolve to false
     * @returns Promise that resolves to true if the account is started, or false if the timeout is reached
     */
    public waitForAccount(accountKey: string, millisecondsTimeout: number): Promise<boolean> {
        if (this.startedAccountKeys.has(accountKey)) {
            return Promise.resolve(true);
        }
        
        return new Promise<boolean>(resolve => {
            // Setup the timeout
            const cancelTimeout = startTimeout(() => {
                const waiters = this.pendingStartWaitsByAccountKey.get(accountKey);
                
                if (waiters) {
                    const index = waiters.findIndex(w => w.resolve === resolve);
                    if (index >= 0) {
                        waiters.splice(index, 1);
                    }
                    
                    if (waiters.length === 0) {
                        this.pendingStartWaitsByAccountKey.delete(accountKey);
                    }
                }
                
                resolve(false);
            }, millisecondsTimeout);
            
            let waiters = this.pendingStartWaitsByAccountKey.get(accountKey);
            if (!waiters) {
                waiters = [];
                this.pendingStartWaitsByAccountKey.set(accountKey, waiters);
            }
            
            waiters.push({ resolve, cancelTimeout });
        });
    }
}

/**
 * We don not want to create an instance of the StartedAccountMonitor until it is needed, but when
 * created we want only one instance to exist in the application.
 */
let theStartedAccountMonitor: StartedAccountMonitor | undefined = undefined;

/**
 * Allow the unit tests to reset the monitor
 */
export function resetStartedAccountMonitorForUnitTests(): void {
    theStartedAccountMonitor = undefined;
}

/**
 * Waits for the account associated with the specified mailbox to be started, or times out after the specified number of milliseconds.
 * @param mailboxInAccount A MailboxInfo object that is associated with the account to wait for
 * @param millisecondsTimeout The number of milliseconds to wait before timing out, when timed out the promise will resolve to false
 * @returns Promise that resolves to true if the account is started, or false if the timeout is reached
 */
export default function waitForAccountToBeStarted(mailboxInAccount: MailboxInfo, millisecondsTimeout: number): Promise<boolean> {
    if (!theStartedAccountMonitor) {
        theStartedAccountMonitor = new StartedAccountMonitor();
    }
    
    const accountKey = getAccountKeyForMailboxInfo(mailboxInAccount);
    return theStartedAccountMonitor.waitForAccount(accountKey, millisecondsTimeout);
}
